import React, { useState, useEffect, MouseEvent, FormEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useLocation } from 'react-router-dom'
import { StopSummary } from 'components/marketplace/StopSummary'
import { Card } from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Typography } from 'transfix-ui/core'
import { IFullStop, IShipmentsState } from 'types'
import { getShipment } from 'utils/actions/shipment'
import { updateShipment } from 'utils/actions/shipments'
import { openDialog } from 'utils/actions/ui'
import { useUserDetails } from 'utils/hooks/useUserDetails'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { TextInput } from 'components/atoms'
import { EditIcon, LaneArrowIcon } from 'transfix-icons'
import { showTCR, findAccessorial } from 'utils/accessorialHelpers'
import { capitalize } from 'utils/capitalize'
import { generateBaseUrl } from 'utils/generateAppDefaults'
import { stopsSummary } from 'utils/shipmentHelpers'
import { isUserType } from 'utils/userTypes'
import { PENDING_STATE, APPROVAL_DIALOG, REJECTION_DIALOG } from '../constants/detention'
import { parseExpirationTime, formatExpirationTime, isExpired } from '../utils/expiresAt'
import {
  selectAccessTokenToken,
  selectShipment,
  selectShipmentUpdateError,
  selectIsDialogOpen,
} from '../utils/hooks/selectors'
import useDetention from '../utils/hooks/useDetention'
import { IDetention } from '../utils/types'
import { DateTimeInputs } from './DateTimeInputs'
import { DetentionDialog } from './DetentionDialog'
import { FacilitySelect } from './FacilitySelect'
import { RequestSummary } from './RequestSummary'
import { ReviewAction } from './ReviewAction'
import { UploadDocument } from './UploadDocument'

export interface ISelector {
  shipmentUpdate: IShipmentsState
}

interface IRequestFormProps {
  accessorialId?: string
}
export const RequestForm = React.memo<IRequestFormProps>(({ accessorialId }) => {
  const { trackEvent } = useMPAnalytics()
  const dispatch = useDispatch()
  const shipment = useSelector(selectShipment)
  const token = useSelector(selectAccessTokenToken)
  const error = useSelector(selectShipmentUpdateError)
  const dialogOpen = useSelector(selectIsDialogOpen)

  const { userDetails, userPermissions } = useUserDetails()
  const { userType = '' } = userDetails || {}

  const [editMode, setEditMode] = useState<boolean>(!!accessorialId)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [selectedStop, setSelectedStop] = useState<IFullStop>()

  const canApproveRejectAccessorials =
    userPermissions?.approveAccessorials && userPermissions?.rejectAccessorials
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const isAdmin = isUserType('admin', userType)
  const expiresAt = parseExpirationTime(useLocation().search)
  const expirationMessage = expiresAt ? formatExpirationTime(expiresAt) : ''
  const easyTokenIsExpired = isExpired(expiresAt)
  const {
    stops = [],
    documents = [],
    equipment,
    accessorials = [],
    info: { shipperId = '' } = {},
  } = shipment || {}

  if (expirationMessage && easyTokenIsExpired)
    global.location.href.replace(global.location.href, generateBaseUrl('detention-review/expired/'))

  const {
    calculatedAmount,
    detention,
    dialogVariant,
    isFormValid,
    onChange,
    onChangeAmount,
    onChangeDateTime,
    onChangeText,
    onClick,
    onConfirm,
    setDetention,
    setDetentionRate,
    updateShipmentAccessorials,
  } = useDetention(selectedStop, equipment)
  useEffect(() => {
    const selectedFacilityStop = stops.find(
      stop => stop.facility.shipperFacilityUuid === detention.shipperFacilityUuid
    )
    if (selectedFacilityStop) setSelectedStop(selectedFacilityStop)
  }, [detention.shipperFacilityUuid, stops])

  const {
    additionalInfo: { externalNote = '', submittedBy = '', trailerControlRecord = '' } = {},
    state: detentionState = '',
  } = detention || {}
  const { appointment, status: { arrivedAt = '', leftAt = '', timezone = '' } = {} } =
    selectedStop || {}
  const hasFile = !!detention.fileName || !!detention.fileFileName
  const hasPod = !!documents?.find(doc => doc.key === 'pod')
  const validFiles = isAdmin ? hasFile || hasPod : hasFile
  const showTCRinput = showTCR(shipperId) && isAdmin
  const submittedByFulfilled = isAdmin ? true : !!submittedBy
  const allowSubmit = isFormValid && submittedByFulfilled
  const stateHeading = detentionState ? `(${capitalize(detentionState)})` : ''
  const heading = `Detention Request ${stateHeading}`
  const classWidth = accessorialId ? 'col-xs-12 col-md-12' : 'col-xs-12 col-md-4'
  const { delivery, pickup } = stopsSummary(shipment)

  useEffect(() => {
    if (shipmentId && !shipment?.id) {
      dispatch(getShipment(shipmentId, token))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId, dispatch, token])
  useEffect(() => {
    if (accessorialId) {
      const accessorialState = findAccessorial(accessorials, accessorialId)
      if (accessorialState) {
        setDetention(accessorialState as IDetention)
      }
    }
  }, [accessorialId, accessorials, setDetention])
  useEffect(() => {
    if (error) {
      setErrorMessage(error)
    }
  }, [error])

  useEffect(() => {
    // if upload POD remove inline Error
    if (validFiles) {
      setErrorMessage('')
    }
  }, [detention.fileName, validFiles])

  const onClickEdit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setEditMode(!editMode)
  }
  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    if (!validFiles) {
      setErrorMessage(`Please upload a POD ${error || ''}`)
    } else {
      const updateAccessorials = updateShipmentAccessorials({
        shipment,
        accessorials,
        detention,
        selectedStop,
        calculatedAmount,
      })
      dispatch(updateShipment(shipmentId, updateAccessorials, token, accessorialId))
      trackEvent('Detention Review', 'SUBMITTED_DETENTION_FORM', { Shipment: shipmentId })
    }
  }

  if (!shipment) return <></>
  return (
    <form onSubmit={handleSubmit}>
      <div className={classWidth}>
        <Card>
          <div className='row'>
            <div className='col-xs-12 col-md-12'>
              <Typography variant='h2' className='fl'>
                {heading}
              </Typography>
              {accessorialId && (
                <div className='fr'>
                  <IconButton
                    Icon={EditIcon}
                    onClick={onClickEdit}
                    label='Edit'
                    data-test='edit-detention'
                    size='large'
                  />
                </div>
              )}
            </div>
          </div>
          {!editMode && (
            <div className='row'>
              <div className='col-xs-12 col-md-12'>
                <span>Load {shipment?.id} - </span>
                {pickup && delivery && (
                  <>
                    <StopSummary stop={pickup} variation='inline-primary' />
                    <LaneArrowIcon className='inline mh1' />
                    <StopSummary stop={delivery} variation='inline-primary' />
                  </>
                )}
              </div>
            </div>
          )}
          <div className='col-xs-12 col-md-12'>
            <FacilitySelect
              stops={stops}
              detention={detention}
              onSelect={setDetention}
              editMode={editMode}
              setSelectedStop={setSelectedStop}
            />
            {detention.shipperFacilityUuid && showTCRinput && (
              <div className='row'>
                <div className='col-md-5 pl0'>
                  {!editMode ? (
                    <TextInput
                      id='trailerControlRecord'
                      label='TCR Number'
                      value={trailerControlRecord}
                      placeholder='Enter TCR'
                      onChange={onChange}
                    />
                  ) : (
                    <>
                      <strong>TCR </strong>
                      <span>{trailerControlRecord}</span>
                    </>
                  )}
                </div>
              </div>
            )}
            {detention.shipperFacilityUuid && selectedStop?.status && (
              <DateTimeInputs
                isAdmin={isAdmin}
                label='Check In'
                timeStamp={detention.startAt || arrivedAt}
                timezone={timezone}
                id='startDateTime'
                onChange={onChangeDateTime}
                editMode={editMode}
              />
            )}
            {detention.shipperFacilityUuid && selectedStop?.status && (
              <DateTimeInputs
                isAdmin={isAdmin}
                label='Check Out'
                timeStamp={detention.endAt || leftAt}
                timezone={timezone}
                id='endDateTime'
                onChange={onChangeDateTime}
                editMode={editMode}
              />
            )}
            {editMode && submittedBy && (
              <div className='row'>
                <div className='col-md-6 pl0'>
                  <strong>Submitted by </strong>
                  <span>{submittedBy}</span>
                </div>
              </div>
            )}
            {editMode && externalNote && (
              <div className='row'>
                <div className='col-md-6 pl0'>
                  <strong>Carrier Note </strong>
                  <span>{externalNote}</span>
                </div>
              </div>
            )}
            {detention.shipperFacilityUuid && (
              <RequestSummary
                isAdmin={isAdmin}
                canApproveRejectAccessorials={canApproveRejectAccessorials}
                editMode={editMode}
                onChange={onChangeAmount}
                detentionAmount={detention.amount}
                calculatedAmount={calculatedAmount}
                setDetentionHourlyRate={setDetentionRate}
                startDateTime={detention.startDateTime || detention.additionalInfo.startDateTime}
                endDateTime={detention.endDateTime || detention.additionalInfo.endDateTime}
                appointment={appointment}
              />
            )}
          </div>
          {detention.shipperFacilityUuid && (
            <div className='col-xs-12 col-md-12 row'>
              {detention.shipperFacilityUuid && (
                <div className='row no-gutter'>
                  <strong>{isAdmin ? 'Documents (Only visible internally)' : 'POD'}</strong>
                  {isAdmin &&
                    documents.map(doc => (
                      <p key={doc.fileName}>
                        <a href={doc.fileUrl} target='_blank' rel='noopener noreferrer'>
                          {doc.fileName}
                        </a>
                      </p>
                    ))}
                </div>
              )}
              {detention.shipperFacilityUuid && (
                <UploadDocument
                  editMode={editMode}
                  fileUrl={detention.fileUrl}
                  fileName={detention.fileName}
                  onChange={setDetention}
                  isAdmin={isAdmin}
                />
              )}
            </div>
          )}
          {detention.shipperFacilityUuid && !isAdmin && (
            <div className='col-xs-12 col-md-12'>
              <div className='row'>
                <div className='col-md-12 pl0'>
                  <label htmlFor='externalNote'>Notes (Optional)</label>
                  <textarea id='externalNote' value={externalNote} onChange={onChangeText} />
                </div>
              </div>
            </div>
          )}
          {detention.shipperFacilityUuid && !isAdmin && (
            <div className='col-xs-12 col-md-12'>
              <div className='row'>
                <div className='col-md-12 pl0'>
                  <TextInput
                    id='submittedBy'
                    label='Email'
                    value={submittedBy}
                    placeholder='Enter email'
                    onChange={onChange}
                  />
                </div>
              </div>
            </div>
          )}
          {editMode && detentionState === PENDING_STATE && canApproveRejectAccessorials && (
            <div className='col-xs-12 col-md-12 row pl0'>
              <div className='col-md-3'>
                <ReviewAction
                  openDialog={openDialog}
                  onClick={onClick}
                  className='btn-secondary btn-full-width'
                  value={REJECTION_DIALOG}
                  name='Reject'
                />
              </div>
              <div className='col-md-3'>
                <ReviewAction
                  openDialog={openDialog}
                  onClick={onClick}
                  className='btn-primary btn-full-width'
                  value={APPROVAL_DIALOG}
                  name='Approve'
                />
              </div>
            </div>
          )}
          {detention.shipperFacilityUuid && !editMode && (
            <>
              <div className='col-xs-12 col-md-12 row'>
                {errorMessage && (
                  <div className='col-md-12 mb5'>
                    <InlineMessage message={errorMessage} type={INLINE_MESSAGE_VARIANTS.ALERT} />
                  </div>
                )}
                <div className='col-md-12'>
                  <button
                    className='btn-primary btn-full-width'
                    type='submit'
                    data-test='submit-detention-request'
                    disabled={!allowSubmit}>
                    Submit Detention Request
                  </button>
                </div>
              </div>
              {expirationMessage && (
                <div className='col-xs-12 col-md-12 row'>
                  <span className='text-secondary'>
                    This form will expire in {expirationMessage}{' '}
                  </span>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
      {dialogOpen && shipment && (
        <DetentionDialog
          accessorialId={accessorialId}
          showTCRinput={showTCRinput}
          trailerControlRecord={trailerControlRecord}
          onConfirm={onConfirm}
          amount={detention.amount}
          variant={dialogVariant}
          openDialog={openDialog}
          shipment={shipment}
        />
      )}
    </form>
  )
})
